import { ReactNode, createContext, useContext, useEffect, useLayoutEffect } from 'react';
import { useContactAccessControl } from '../useContactAccessControl';
import { ContactAccessControl } from '../../types/dashboard';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getUser } from '../../redux/slices/contactSlice';
import { AppDispatch, RootState } from '../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { useMsal } from '@azure/msal-react';

export enum AccessControl {
  Normal = '',
  Watched = '',
  Delayed = 'Delayed',
  Suspended = 'Suspended',
  Locked = 'Locked',
}

type MessageObject = {
  red: string;
  orange: string;
  yellow: string;
  photo: string;
  [key: string]: string; 
};

type ProviderMessageObject = {
  red: string;
  orange: string;
  yellow: string;
  [key: string]: string;
};

export const getBannerColor = (accessControl: string) => {
  return accessControl === AccessControl.Locked
    ? 'red'
    : accessControl === AccessControl.Suspended
    ? 'orange'
    : accessControl === AccessControl.Delayed
    ? 'yellow'
    : '';
};

interface IGlobalContext {
  CandidateAccessControl: string;
  ProviderAccessControl: string;
  CandidateBannerColor: string;
  ProviderBannerColor: string;
  ProviderBannerMessage: string;
  CandidateBannerMessage: string;
  PhotoBannerMessage: string;
  photoBannerVisible: boolean;
  Color: string;
  FullName: string;
  CandidateId: string;
  loading?: boolean;
  error?: string;
}

const GlobalContext = createContext<IGlobalContext | undefined>(undefined);

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobalContext must be used within a GlobalContextProvider');
  }
  return context;
};

interface GlobalContextProviderProps {
  children: ReactNode;
}

export const GlobalContextProvider = ({ children }: GlobalContextProviderProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { accounts } = useMsal();
  const isAuthenticated = accounts.length > 0;

  const accessControlQuery = useContactAccessControl();
  const accessControlData = accessControlQuery.data?.data?.Data as ContactAccessControl;

  const CandidateAccessControl = accessControlData?.CandidateAccessControl || AccessControl.Normal;
  const ProviderAccessControl = accessControlData?.ProviderAccessControl || AccessControl.Normal;

  const candidateBannerColor = getBannerColor(CandidateAccessControl);
  const providerBannerColor = getBannerColor(ProviderAccessControl);

  const { contact, loading: contactLoading, error: contactError } = useSelector((state: RootState) => state.contact);
  const dispatch = useDispatch<AppDispatch>();
  useEffect(() => {
    if (isAuthenticated && contact === null && !contactLoading && !contactError) {
      dispatch(getUser());
    }
  }, [isAuthenticated]);

  const photoBannerVisible = contact !== null && !contactError
    && !contact.ContactPhotoRequest
    && !contact.myCCOPicture;

  const candidateBannerVisible = !!candidateBannerColor;
  const providerBannerVisible = !!providerBannerColor;

  useLayoutEffect(() => {
    const sidebar = document.getElementById('ccoSidebar') as HTMLElement | null;
    if (!sidebar) return;
    const visibleCount = [photoBannerVisible, candidateBannerVisible, providerBannerVisible].filter(Boolean).length;
    sidebar.style.paddingTop = visibleCount > 0 ? `${visibleCount * 90}px` : '';
    return () => { sidebar.style.paddingTop = ''; };
  }, [photoBannerVisible, candidateBannerVisible, providerBannerVisible, pathname]);

  const providerBannerMessage: ProviderMessageObject = {
    red: t('Banner.testingpersonalBanner.red'),
    orange: t('Banner.testingpersonalBanner.orange'),
    yellow: t('Banner.testingpersonalBanner.yellow'),
  };

  const candidateBannerMessage: MessageObject = {
    red: t('Banner.CandidateAccessControl.red'),
    orange: t('Banner.CandidateAccessControl.orange'),
    yellow: t('Banner.CandidateAccessControl.yellow'),
    photo: t('Banner.CandidateAccessControl.note'),
  };

  const contextValues: IGlobalContext = {
    CandidateAccessControl,
    ProviderAccessControl,
    CandidateBannerColor: candidateBannerColor,
    ProviderBannerColor: providerBannerColor,
    ProviderBannerMessage: providerBannerMessage[providerBannerColor] || '',
    CandidateBannerMessage: candidateBannerMessage[candidateBannerColor] || '',
    PhotoBannerMessage: candidateBannerMessage.photo,
    photoBannerVisible: !!photoBannerVisible,
    Color: (candidateBannerColor === 'yellow' || providerBannerColor === 'yellow') ? 'black' : '',
    CandidateId: accessControlData?.CandidateId || '',
    FullName: accessControlData?.FullName || '',
    loading: accessControlQuery.isLoading,
    error: accessControlQuery.error?.message,
  };
  return (
    <GlobalContext.Provider value={contextValues}>
      {children}
    </GlobalContext.Provider>
  );
};

